/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import styles from './styles';
import { Box, Dialog, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { CancelPresentationRounded, ChevronLeft, ChevronRight, ShortcutOutlined } from '@mui/icons-material';
import CanvasJSReact from '../../canvasjs/canvasjs.react';
import Datagrid from '../../Datagrid';
import columns from './table_config';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { useDispatch, useSelector } from 'react-redux';
import { downloadDataReport, downloadRampSchedulePDF, listHydraulicsDetails } from '../../../../store/actions/hydraulics';
import { COLORS } from '../../../../shared/config/colors';
import ReportInputsModal from '../ReportInputsModal';
import { TOOLTIPS } from '../../../../utils/constants';
import HydraulicsDetailsModal from '../HydraulicsRunModalDialog';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import ReportsDownloadSelector from '../ReportsDownloadSelector';
import VersionText from '../VersionText';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const PumpRampModel = ({
  onCloseClick,
  isOpen,
  pumpRamps,
  wellDetails,
  pumpRampUnits,
  tableList,
  setTableList,
  hydraulicWellsDetails,
  phVersion
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const { unit } = useUnitConversion();
  const hydraulics = useSelector((state) => state.hydraulics);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [hasNoCuttings, setHasNoCuttings] = useState(false);
  const [targetECDWithSBP, setTargetECDWithSBP] = useState(false);
  const [showReportInput, setShowReportInputs] = useState(false);
  const [reportPreview, setReportPreview] = useState(false);
  const [showHydraulicsParameterDialog, setShowHydraulicsParameterDialog] = useState({
    open: false,
    data: {},
    dialogParameter: ''
  });

  useEffect(() => {
    if (loader || hydraulics?.loading) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader, hydraulics]);

  useEffect(() => {
    const pumpData = pumpRamps?.results?.pump_ramps || [];

    const records = pumpData.map((record, index) => ({
      ...record,
      sbp: Number(record?.sbp?.toFixed?.(0)),
      no_cuttings_sbp: record?.no_cuttings_sbp ?? null,
      target_name: record?.target_name ?? null,
      target_ecd_with_sbp: record?.target_ecd_with_sbp ?? null,
      _id: Math.random().toString(36).substring(2, 10),
      steps: index + 1,
    }));
    const hasCuttings = pumpData.some(
      (record) => record?.no_cuttings_sbp !== null && record?.no_cuttings_sbp !== undefined
    );
    const hasTargetECDWithSBP = pumpData.some(
      (record) => record?.target_ecd_with_sbp !== null && record?.target_ecd_with_sbp !== undefined
    );
    setHasNoCuttings(hasCuttings);
    setTargetECDWithSBP(hasTargetECDWithSBP);
    setTableRows(records);
    setShowTable(true);
  }, [pumpRamps]);

  const options = {
    height: 430,
    animationEnabled: true,
    theme: 'light2',
    axisY3: {
      title: `Clean-Mud SBP (${pumpRampUnits?.no_cuttings_sbp?.label})`,
      includeZero: true,
    },
    axisY: {
      title: `SBP (${pumpRampUnits?.sbp?.label})`,
      includeZero: true,
    },
    axisY2: {
      title: `Flow In (${pumpRampUnits?.flow_in?.label})`,
    },
    axisX: {
      title: 'Steps',
    },
    data: [
      {
        type: 'column',
        showInLegend: true,
        name: `Flow In (${pumpRampUnits?.flow_in?.label})`,
        color: '#c0d0e5',
        toolTipContent: `Flow In: {y} ${pumpRampUnits?.flow_in?.label}`,
        axisYType: 'secondary',
        dataPoints: pumpRamps?.results?.pump_ramps.map((dataPoint) => ({
          label: 'Flow In',
          y: dataPoint.flow_in,
        })),
      },
      {
        type: 'line',
        name: `SBP (${pumpRampUnits?.sbp?.label})`,
        showInLegend: true,
        color: '#1D3956',
        axisYType: 'primary',
        toolTipContent: `SBP: {y} ${pumpRampUnits?.sbp?.label}`,
        dataPoints: pumpRamps?.results?.pump_ramps.map((dataPoint, index) => ({
          label: `${index + 1}`,
          y: dataPoint.sbp,
        })),
      },
      {
        type: 'line',
        name: `Clean-Mud SBP (${pumpRampUnits?.no_cuttings_sbp?.label})`,
        showInLegend: hasNoCuttings,
        color: COLORS.ROYAL_BLUE,
        axisYType: 'primary',
        toolTipContent: `Clean-Mud SBP: {y} ${pumpRampUnits?.no_cuttings_sbp?.label}`,
        dataPoints: pumpRamps?.results?.pump_ramps.map((dataPoint, index) => ({
          label: `${index + 1}`,
          y: dataPoint.no_cuttings_sbp,
        })),
      },
    ],
  };

  const handleReportPdf = () => {
    const body = {
      inputs: tableList,
      jobId: pumpRamps?.jobId,
      wellsInfoId: pumpRamps?.wellsInfoId,
      runId: pumpRamps?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: pumpRamps?.name
    };
    setLoader(true);
    dispatch(downloadRampSchedulePDF(body, reportPreview, () => {
      setLoader(false);
    }));
  };
  const targetName = tableRow.find((row) => row?.target_name)?.target_name;

  const handleDataReportDownload = () => {
    setLoader(true);
    const body = {
      jobId: pumpRamps?.jobId,
      wellsInfoId: pumpRamps?.wellsInfoId,
      runId: pumpRamps?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: pumpRamps?.name,
      type: 'rampSchedule',
    };
    dispatch(downloadDataReport(body, () => {
      setLoader(false);
    }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseClick}
      maxWidth={'lg'}
      fullWidth={true}
      style={{
        marginRight: '20px'
      }}
      PaperProps={{
        sx: {
          overflow: 'visible'
        },
      }}
    >
      <Box className={classes.dialogTitle}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              {`${wellDetails?.Rig} | ${wellDetails?.Well} | ${pumpRamps?.name}`}
            </Grid>
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
            {tableRow?.length
              ? (
                  <ReportsDownloadSelector
                    loading={loader || hydraulics.downloadRampSchedulePDF === 'loading' || hydraulics?.loading}
                    onDataDownload={handleDataReportDownload}
                    onPreviewOpen={(previewItem) => {
                      setShowReportInputs(true);
                      setReportPreview(previewItem);
                    }}
                  />
                )
              : null}
              <Tooltip title={TOOLTIPS.viewRunInputs}>
                <span>
                  <IconButton
                    disabled={hydraulics.downloadRampSchedulePDF === 'loading'}
                    onClick={() => {
                      dispatch(listHydraulicsDetails(pumpRamps));
                      setShowHydraulicsParameterDialog({
                        open: true,
                        data: pumpRamps,
                        dialogParameter: ''
                      });
                    }}>
                    <ShortcutOutlined fontSize='large' />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                onClick={() => {
                  onCloseClick();
                }}
              >
                <CancelPresentationRounded fontSize='large' />
              </IconButton>
            </Grid>
          </Grid>
        </Typography>
      </Box>
      <Divider />
      <Grid container item xs={showTable ? 8 : 12} alignItems="center" justifyContent="center">
        <Grid item style={{ fontWeight: 'bold', fontSize: '1.3rem', marginTop: showTable ? '3%' : '2.5%' }}>
          {'Ramp Schedule'}
        </Grid>
      </Grid>
       {tableRow?.length > 0
         ? (<>
      <Box style={{
        overflow: 'auto',
        marginTop: '-4%'
      }}>
        <Grid container>
          <Grid item xs={showTable ? 8 : 12} sx={{
            p: 2,
            pt: 10,
            '.canvasjs-chart-container canvas': {
              cursor: loader || hydraulics?.loading ? 'wait !important' : ''
            }
          }}>
            <CanvasJSChart options={options} />
          </Grid>
          {!showTable && <Box className={classes.menuTogglerBtn}>
            <IconButton
              onClick={() => {
                setShowTable(!showTable);
              }}>
              <ChevronLeft sx={{ fontSize: '33px' }} />
            </IconButton>
          </Box>}
          {showTable && <Grid item xs={4} marginTop={'2.5%'}>
            <Box className={classes.menuTogglerTableBtn}>
              <IconButton
                onClick={() => {
                  setShowTable(!showTable);
                }}>
                <ChevronRight sx={{ fontSize: '33px' }} />
              </IconButton>
            </Box>
            {showTable && <Grid item xs={12}>
              <Datagrid
                fileName={`${wellDetails?.Well?.trim()}-${pumpRamps?.name?.trim()}-Pump-Ramp`}
                loading={false}
                data={tableRow}
                sx={{
                  '.MuiDataGrid-cell': {
                    fontSize: '1rem',
                    padding: '2px',
                  },
                  '.MuiDataGrid-columnHeader': {
                    fontSize: '1.2rem',
                    fontWeight: '900 !important',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    overflow: 'visible',
                    lineHeight: '1.43rem',
                    whiteSpace: 'normal',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '0px'
                  },
                }}
                columns={columns(pumpRampUnits, unit, hasNoCuttings, targetECDWithSBP, targetName)}
                disableStickyHeader={true}
                autoHeight={false}
                hideFooterPagination
                hideFooterSelectedRowCount
                height={'500px'}
                getRowHeight={() => 'auto'}
                pageSize={100}
                toolbar={{
                  options: {
                    columns: false,
                    filters: false,
                    density: false,
                    export: {
                      enabled: true,
                    }
                  },
                }}
              />
            </Grid>}
          </Grid>}
        </Grid>
      </Box></>)
         : <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50vh',
              textAlign: 'center',
              p: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
            >
              No Results Found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.disabled' }}
            >
              Try adjusting parameters to generate results.
            </Typography>
          </Box>
        </>}
      {
        showReportInput
          ? <ReportInputsModal
            isOpen={showReportInput}
            tableList={tableList}
            setTableList={setTableList}
            onCloseClick={() => {
              setShowReportInputs(false);
            }}
            generateReport={handleReportPdf}
          />
          : null
      }
      {showHydraulicsParameterDialog.open && showHydraulicsParameterDialog?.data?.name
        ? (
          <HydraulicsDetailsModal
            selectedJob={wellDetails}
            isOpen={showHydraulicsParameterDialog.open}
            onCloseClick={() => {
              setShowHydraulicsParameterDialog({
                open: false,
                data: {}
              });
              dispatch({
                type: REDUX_ACTIONS.HYDRAULICS_SET_ID,
                payload: {
                  data: '',
                },
              });
            }}
            data={showHydraulicsParameterDialog.data}
            dialogParameter={showHydraulicsParameterDialog.dialogParameter}
            wellDetails={hydraulicWellsDetails}
          />
          )
        : null}
        <VersionText version={phVersion} loading={false} />
    </Dialog>
  );
};
PumpRampModel.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  pumpRamps: PropTypes.any,
  wellDetails: PropTypes.any,
  pumpRampUnits: PropTypes.any,
  tableList: PropTypes.any,
  setTableList: PropTypes.any,
  hydraulicWellsDetails: PropTypes.any,
  phVersion: PropTypes.string
};
PumpRampModel.defaultProps = {
  selectedJob: {}
};

export default PumpRampModel;
